//============================================================================
// Name        : EditSchedule.tsx
// Purpose     : Implement Schedule UI
// Created     : 2019-08-16
// Author      : Senitron Team
// Copyright   : Copyright 2019 Senitron Corporation. All rights reserved.
//============================================================================

// tslint:disable-next-line: no-unused-expression
/** @jsx jsx */ jsx;
import { css, jsx } from "@emotion/core";
import _ from "lodash";
import moment from "moment";
import React from "react";
import { connect } from "react-redux";
import Switch from "react-switch";
import { IUpdateStateAction } from "./actions/editScheduleActions";
import ConfirmDialog from "./ConfirmDialog";
import EditScheduleDialog from "./EditScheduleDialog";
import ICActionRow from "./ICActionRow";
import ICRow from "./ICRow";
import Table from "./Table";
import TableColumn from "./TableColumn";
import TableColumnSimple from "./TableColumnSimple";
import { ChangeInputEvent, EditScheduleActions, IEditScheduleState, IProduction, IReduxReducers, IUser } from "./types";

const switchStyle = css`
    display: flex;
    align-items: center;
`;
const leftBlueCell = css`
    background-color: #2770a8;
    color: #ffffff;
    padding-left: 15px;
    padding-right: 15px;
    padding-top: 5px;
    padding-bottom: 5px;
    text-align: left;
    font-size: 12px;
`;
const lightBlueCell = css`
    background-color: #6194bd;
`;
const lightestBlueCell = css`
    background-color: #8ab4d5;
`;
const borderedCell = css`
    border: 1px solid #ccc;
    border-top: none;
    padding: 5px;
`;
const buildingBlock = css`
    margin-bottom: 25px;
    width: 360px;
`;
const scheduleBtn = css`
    font-size: 13px;
    padding-top: 3px;
    padding-bottom: 3px;
`;

interface IEditScheduleTmpState {
    width: number;
}

class EditSchedule extends React.Component<IEditScheduleState, IEditScheduleTmpState> {
    constructor(props: IEditScheduleState) {
        super(props);
        // Save properties sent from Rails in the wrapper into the store
        this.props.storeInitialState(props);
        this.state = { width: 0 };

        // Force the sidebar to close
        setTimeout(() => {
            const bodyEl = document.getElementsByTagName("body")[0];
            if (!bodyEl.classList.contains("hidden-menu")) {
                $(document).ready(() => $("#hide-menu a").trigger("click"));
            }
        // tslint:disable-next-line: align
        }, 500);

        this.getPastDueDates();
    }

    resizeUpdate = () => {
        setTimeout(() => {
            const containerEl = document.getElementById("allContainer");
            let width = containerEl ? containerEl.offsetWidth : window.innerWidth;

            const bodyEl = document.getElementsByTagName("body")[0];
            if (bodyEl.classList.contains("hidden-menu")) {
                width -= 980;
            }
            else {
                width -= 880;
            }
            this.setState({ width });
        // tslint:disable-next-line: align
        }, 500);
    }

    componentWillMount() {
        this.resizeUpdate();
    }

    componentDidMount() {
        window.addEventListener("resize", this.resizeUpdate);
        $("#hide-menu a").on("click", this.resizeUpdate);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.resizeUpdate);
    }

    getUserName = (user: IUser) => `${user.first_name} ${user.last_name}`;

    onChange = (event: ChangeInputEvent) => this.props.updateState({ [event.target.name]: event.target.value });

    onToggleLbsDisplay = (value: boolean) => this.props.updateState({ showAsLbs: value });

    onToggleCaseDisplay = (value: boolean) => this.props.updateState({ showAsCases: value });

    scheduleProduction = (user: IUser) => this.props.showScheduleProduction(user);

    getDateRange = (): string => `${this.props.startDay} - ${this.props.endDay} Days`;

    getTotalsTitle = (): string => `TOTAL ${this.getDateRange()}`;

    parseServerDate = (serverDate: string): string => moment(serverDate).format("MM/DD/YYYY");

    getProductionDates = (): string[] => {
        const newDates = [];
        let startDate = moment().add(this.props.startDay, "days");

        const days = (this.props.endDay + 1) - this.props.startDay;
        const it = Array(days).fill(1);
        for (const i of it) {
            newDates.push(startDate.format("YYYY-MM-DD"));
            startDate = startDate.add(i, "days");
        }
        return newDates;
    }

    sumProductionsTotal = (): string => {
        return String(this.props.productions.reduce(
            (accTotal, production: IProduction) => accTotal + production.needed_qty, 0));
    }

    sumProductionsForDate = (date: string): string => {
        const dayTotal = this.props.productions.filter(p => p.scheduled_date == date)
            .reduce((accTotal, production: IProduction) => accTotal + production.pending_qty, 0);
        return String(dayTotal);
    }

    getProductionForDate = (date: string): number[] => {
        const prods = [];
        this.props.schedulerUsers.map(user =>
            prods.push(
                this.props.productions.filter(p => p.scheduled_date == date && p.scheduler_user_id == user.id)
                    .reduce((accTotal, p) => accTotal + p.needed_qty, 0)));
        return prods;
    }

    sumProductionsForUser = (userId: number) => {
        const futurePending = this.props.productions.filter(p => p.scheduler_user_id == userId)
            .reduce((acc, p) => acc + p.needed_qty, 0);
        const pastPending = this.props.pastDueProductions.filter(p => p.scheduler_user_id == userId)
            .reduce((accTotal, p) => accTotal + p.needed_qty, 0);
        return futurePending + pastPending;
    }

    sumProductionsTotalAndPastDue = (): number => {
        return this.props.schedulerUsers.reduce((acc, u) => acc + this.sumProductionsForUser(u.id), 0);
    }

    sumPastDueProductionsForDate = (date: string): string => {
        const dayTotal = this.props.pastDueProductions.filter(p => p.scheduled_date == date)
            .reduce((accTotal, production: IProduction) => accTotal + production.needed_qty, 0);
        return String(dayTotal);
    }

    getPastDueForDate = (date: string): number[] => {
        const userPastProd = [];
        this.props.schedulerUsers.map(user =>
            userPastProd.push(
                this.props.pastDueProductions.filter(p => p.scheduled_date == date && p.scheduler_user_id == user.id)
                    .reduce((accTotal, p) => accTotal + p.needed_qty, 0)));
        return userPastProd;
    }

    sumPastDueProductionsTotal = (): string => {
        return String(this.props.pastDueProductions.reduce(
            (accTotal, production: IProduction) => accTotal + production.needed_qty, 0));
    }

    getPastDueDates = (): string[] => {
        const unique = (val, idx, self) => self.indexOf(val) == idx;
        return this.props.pastDueProductions.map(p => p.scheduled_date).filter(unique);
    }

    valueInUnits = (value: number): JSX.Element => {
        const cases = value / parseFloat(this.props.storageUnit.measure_unit);
        return (
            <React.Fragment>
                {this.props.showAsLbs && <div>{value}</div>}
                {this.props.showAsCases && <div>{cases}</div>}
            </React.Fragment>
        );
    }

    render() {
        const havePastDue = this.props.pastDueProductions.length > 0;
        return (
            <React.Fragment>
                <div css={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <div css={{ display: "flex", alignItems: "baseline" }}>
                        <div css={{ fontSize: 34, color: "#646464" }}>{this.props.product.alu}</div>
                        <div css={{ fontSize: 20, marginLeft: 20 }}>{this.props.product.description}</div>
                    </div>

                    <div css={{ display: "flex", alignItems: "baseline" }}>
                        <span css={{ marginRight: 15 }}>Show UOM in</span>
                        <div css={switchStyle}>
                            <span css={switchStyle}>
                                <span css={{ fontSize: "16px", marginRight: "10px", marginLeft: "10px" }}>Lbs.</span>
                                <Switch onChange={this.onToggleLbsDisplay} checked={this.props.showAsLbs} />
                            </span>
                            <span css={switchStyle}>
                                <span css={{ fontSize: "16px", marginRight: "10px", marginLeft: "10px" }}>Cases</span>
                                <Switch onChange={this.onToggleCaseDisplay} checked={this.props.showAsCases} />
                            </span>
                        </div>
                    </div>
                </div>

                <div id="allContainer" css={{ display: "flex", justifyContent: "flex-start" }}>

                    <div>
                        {/* Schedulers block */}
                        <div css={buildingBlock}>
                            <Table totals={false}>
                                <TableColumnSimple width={360} headerHeight={30} headerTitle="Scheduler" footer="TOTAL SCHEDULED QTY">
                                    {this.props.schedulerUsers.map((user: IUser, key) =>
                                        <ICActionRow key={key} content={this.getUserName(user)} showDividers={true}>
                                            <button css={scheduleBtn} onClick={() => this.scheduleProduction(user)} className="man-btn man-green-btn">Schedule</button>
                                        </ICActionRow>
                                    )}
                                </TableColumnSimple>
                            </Table>
                        </div>

                        {/* Buildings */}
                        {this.props.zones.map((zone, key) =>
                            <div key={key} css={buildingBlock}>
                                <div css={[leftBlueCell]}>
                                    <div>{zone.name}</div>
                                </div>
                                <div css={borderedCell}>
                                    Qty Scheduled
                                    {this.props.showAsLbs && this.props.showAsCases && <div>&nbsp;</div>}
                                </div>
                                <div css={borderedCell}>
                                    Qty Completed
                                    {this.props.showAsLbs && this.props.showAsCases && <div>&nbsp;</div>}
                                </div>
                                <div css={borderedCell}>
                                    Remaining Planed Qty
                                    {this.props.showAsLbs && this.props.showAsCases && <div>&nbsp;</div>}
                                </div>
                            </div>
                        )}

                        {/* Total Completed */}
                        <div css={[buildingBlock, leftBlueCell, lightestBlueCell, { marginTop: 53 }]}>
                            TOTAL QTY COMPLETED
                            {this.props.showAsLbs && this.props.showAsCases && <div>&nbsp;</div>}
                        </div>

                        {/* Total Pending */}
                        <div css={[buildingBlock, leftBlueCell, lightBlueCell]}>
                            TOTAL REMAINING SCHEDULE QTY
                            {this.props.showAsLbs && this.props.showAsCases && <div>&nbsp;</div>}
                        </div>

                        {/* Percentage Pending */}
                        <div css={[buildingBlock, leftBlueCell, lightBlueCell]}>% REMAINING TO COMPLETE SCHEDULED QTY</div>
                    </div>

                    {/* Past Due */}
                    {havePastDue &&
                        <div css={{ marginTop: 15, marginLeft: 20, minWidth: 205 }}>
                            {/* Load past dues from all time  */}
                            <Table scrollable={true} title="Past-Due" totals={true}
                                totalsHeaderTitle="TOTAL" totalsHeaderSubtitle="Past-Due"
                                totalsFooter={this.sumPastDueProductionsTotal()}
                                maxWidth={this.state.width}>

                                {this.getPastDueDates().map((date, i) =>
                                    <TableColumn key={i} headerHeight={30}
                                        headerTitle={this.parseServerDate(date)}
                                        footer={this.sumPastDueProductionsForDate(date)}
                                        date={date} pastDue={true}>

                                        {this.getPastDueForDate(date).map((val, key) =>
                                            <ICRow key={key} content={this.valueInUnits(val)} showDividers={true} />
                                        )}
                                    </TableColumn>
                                )}
                            </Table>
                        </div>
                    }

                    <div css={{ marginTop: 15, marginLeft: 20 }}>
                        {/* Calendar View */}
                        <Table scrollable={true} title={this.getDateRange()} totals={true}
                            totalsHeaderTitle="TOTAL" totalsHeaderSubtitle={this.getDateRange()}
                            totalsFooter={this.sumProductionsTotal()}
                            maxWidth={this.state.width}>

                            {this.getProductionDates().map((date, i) =>
                                <TableColumn key={i} headerHeight={30}
                                    headerTitle={this.parseServerDate(date)}
                                    footer={this.sumProductionsForDate(date)}
                                    date={date} pastDue={false}>

                                    {this.getProductionForDate(date).map((val, key) =>
                                        <ICRow key={key} content={this.valueInUnits(val)} showDividers={true} />
                                    )}
                                </TableColumn>
                            )}
                        </Table>
                    </div>

                    <div css={{ marginTop: 0, marginLeft: 20, minWidth: 130 }}>
                        {/* Totals */}
                        <Table totals={false}>
                            <TableColumn headerHeight={30} headerTitle={this.getTotalsTitle()}
                                headerSubtitle="+ Past Due"
                                footer={String(this.sumProductionsTotalAndPastDue())}
                                pastDue={false}>

                                {this.props.schedulerUsers.map((user: IUser, key) =>
                                    <ICRow key={key} content={this.valueInUnits(this.sumProductionsForUser(user.id))} showDividers={true} />
                                )}
                            </TableColumn>
                        </Table>
                    </div>
                </div>

                <EditScheduleDialog />

                <ConfirmDialog
                    show={this.props.showConfirmDialog}
                    title={this.props.confirmDialogTitle}
                    message={this.props.confirmDialogMsg}
                    positiveLbl="Yes"
                    negativeLbl="No" />

            </React.Fragment>
        );
    }
}

const mapStateToProps = (state: IReduxReducers) => ({
    // schedulerUsers:    state.editSchedule.schedulerUsers
    showAsCases: state.editSchedule.showAsCases,
    showAsLbs:   state.editSchedule.showAsLbs,

    // ------------------------------------------
    // Schedule Production Dialog
    // ------------------------------------------
    // showScheduleProdDialog: state.editSchedule.showScheduleProdDialog,

    // ------------------------------------------
    // Confirmation Dialog
    // ------------------------------------------
    showConfirmDialog:    state.editSchedule.showConfirmDialog,
    confirmDialogTitle:   state.editSchedule.confirmDialogTitle,
    confirmDialogMsg:     state.editSchedule.confirmDialogMsg,
    confirmDlgPositiveCb: state.editSchedule.confirmDlgPositiveCb,
    confirmDlgNegativeCb: state.editSchedule.confirmDlgNegativeCb
});

const mapDispatchToProps = dispatch => ({
    storeInitialState: (props: IEditScheduleState) => dispatch({
        type: EditScheduleActions.StoreInitialState,
        payload: props
    } as IUpdateStateAction),
    updateState: (newState: object) => dispatch({
        type: EditScheduleActions.UpdateState,
        payload: newState
    } as IUpdateStateAction),
    showScheduleProduction: (user: IUser) => {
        return dispatch({
            type: EditScheduleActions.ShowScheduleProduction,
            payload: {
                showScheduleProdDialog: true,
                scheduleDialogUserId: user.id,
                scheduleDialogUsername: `${user.first_name} ${user.last_name}`
            }
        });
    }
});

export default connect(mapStateToProps, mapDispatchToProps)(EditSchedule);
